import type { StripeElements } from '@stripe/stripe-js';

import type StripeApi from '../../services/stripe/stripe';
import {
  findFirstInvalidField,
  focusElement,
  focusFirstInvalidFieldWithRetry,
  isElementBefore,
} from './focusFirstInvalidField';

const STRIPE_PAYMENT_ELEMENT_SELECTOR = '#paymentElement';

type FocusFirstStripeFormErrorParams = {
  errors: Partial<Record<string, string>>;
  selectorByErrorKey: Partial<Record<string, string>>;
  hasStripeError: boolean;
  elements: StripeElements | null;
  stripeApi: typeof StripeApi;
  // Scopes lookups to a form's subtree so a duplicate `#paymentElement`
  // from another mounted form (e.g. a background screen behind a
  // secondary drawer) can't be matched instead.
  root?: ParentNode;
};

/**
 * Focuses whichever comes first in the DOM: an invalid platform field
 * or the Stripe payment element (which focuses its own first invalid field).
 */
export const focusFirstStripeFormError = ({
  errors,
  selectorByErrorKey,
  hasStripeError,
  elements,
  stripeApi,
  root = document,
}: FocusFirstStripeFormErrorParams) => {
  const firstInvalidField = findFirstInvalidField({
    errors,
    selectorByErrorKey,
    root,
  });
  const stripeContainer = root.querySelector<HTMLElement>(
    STRIPE_PAYMENT_ELEMENT_SELECTOR,
  );
  const canFocusStripeError =
    hasStripeError && Boolean(elements);

  if (
    firstInvalidField &&
    (!canFocusStripeError ||
      !stripeContainer ||
      isElementBefore(firstInvalidField, stripeContainer))
  ) {
    focusElement(firstInvalidField);
    return;
  }

  if (canFocusStripeError && elements) {
    stripeApi.focusFirstValidationError(elements, root);
    return;
  }

  if (Object.keys(errors).length > 0) {
    focusFirstInvalidFieldWithRetry({
      errors,
      selectorByErrorKey,
      root,
    });
  }
};

export default focusFirstStripeFormError;
